import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { commitDraft, commitSessionPlan, type SessionPlanDraft } from '@4cm/brain/draftCommit';
import { recordGenerationObservation } from '@4cm/brain/generationContext';
import { findTechnicalDrillsMissingDecision } from '@4cm/core/sessionPlannerPrompt';
import type { CurriculumPlan, PhasePlan, WeeklyTopic } from '@4cm/core/curriculumPrompt';
import { BrainOrb, BrainPrompt, Button, Card, GlassPill } from '../components/primitives';
import { DraftCard } from '../components/DraftCard';
import { useAuth } from '../contexts/AuthContext';
import { useTeamFull, type TeamFull } from '../lib/queries/useTeamFull';
import { useCalibration } from '../contexts/CalibrationContext';
import { useBrain, type BrainTurn } from '../lib/brain';
import { useBrainAutoSpeak } from '../lib/useBrainAutoSpeak';
import { useVoiceToText } from '../lib/voiceInput';
import { supabase } from '../lib/supabase';
import { generateWeekSessions, type WeekPlanResult } from '../lib/weeklyPlanner';
import './page-shell.css';
import './Season.css';

const FIRST_PROMPT =
  "Let's map your season. Tell me how long you want to plan for and what matters most — or just say “plan my season” and I'll draft a multi-week theme plan you can tweak.";

const CORNER_META: Record<string, { label: string; color: string }> = {
  technical_tactical: { label: 'Tech', color: 'var(--c-tech)' },
  physical: { label: 'Phys', color: 'var(--c-phys)' },
  psychological: { label: 'Psych', color: 'var(--c-psych)' },
  social: { label: 'Social', color: 'var(--c-social)' },
};

/** True when a curriculum_plans row holds a real CurriculumPlan (phase_plans),
 *  not the lighter shape onboarding writes. */
function isRealPlan(data: unknown): data is CurriculumPlan {
  return (
    !!data &&
    typeof data === 'object' &&
    Array.isArray((data as { phase_plans?: unknown }).phase_plans) &&
    (data as { phase_plans: unknown[] }).phase_plans.length > 0
  );
}

export function Season() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { team } = useTeamFull();
  const { level } = useCalibration();

  const brain = useBrain({
    teamId: team?.id ?? null,
    screen: { kind: 'curriculum', teamId: team?.id ?? null, note: 'Planning the season' },
  });
  const [intro] = useState<BrainTurn>({ id: 'intro', role: 'assistant', text: FIRST_PROMPT });
  const [committing, setCommitting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Active committed plan (null = none / onboarding-shape only → show CTA).
  const [plan, setPlan] = useState<CurriculumPlan | null>(null);
  const [planId, setPlanId] = useState<string | null>(null);
  const [planLoading, setPlanLoading] = useState(true);

  // Inline edit of the committed plan ("tweak"). draft = working copy.
  const [editing, setEditing] = useState(false);
  const [draftPlan, setDraftPlan] = useState<CurriculumPlan | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);

  // Which week's session review is open.
  const [weekReview, setWeekReview] = useState<{ week: WeeklyTopic; phaseLabel: string } | null>(null);

  const autoSpeak = useBrainAutoSpeak({ turns: brain.turns, loading: brain.loading });
  const voice = useVoiceToText({
    onTranscript: (t) => {
      autoSpeak.markVoiceTurn();
      void brain.send(t);
    },
  });

  const loadActivePlan = useCallback(async () => {
    if (!team?.id) {
      setPlan(null);
      setPlanId(null);
      setPlanLoading(false);
      return;
    }
    setPlanLoading(true);
    try {
      const { data } = await supabase
        .from('curriculum_plans')
        .select('id, plan_data')
        .eq('team_id', team.id)
        .eq('is_active', true)
        .maybeSingle();
      const pd = (data as { id?: string; plan_data?: unknown } | null)?.plan_data;
      if (data && isRealPlan(pd)) {
        setPlan(pd);
        setPlanId((data as { id: string }).id);
      } else {
        setPlan(null);
        setPlanId(null);
      }
    } catch {
      setPlan(null);
      setPlanId(null);
    } finally {
      setPlanLoading(false);
    }
  }, [team?.id]);

  useEffect(() => { void loadActivePlan(); }, [loadActivePlan]);

  // Deep-link from Calendar "Plan this week" — ?week=YYYY-MM-DD opens that
  // week's review once the plan is loaded.
  useEffect(() => {
    const weekParam = searchParams.get('week');
    if (!weekParam || !plan) return;
    for (const phase of plan.phase_plans) {
      const match = phase.weekly_topics?.find((w) => w.date_start === weekParam);
      if (match) {
        setWeekReview({ week: match, phaseLabel: phase.phase });
        return;
      }
    }
  }, [searchParams, plan]);

  const handleApproveCurriculum = useCallback(async (turnId: string) => {
    const turn = brain.turns.find((t) => t.id === turnId);
    if (!turn?.draft || !user) return;
    setCommitting(turnId);
    setError(null);
    try {
      const result = await commitDraft(turn.draft.type, turn.draft.payload, {
        teamId: team?.id ?? null,
        coachId: user.id,
      });
      if (!result.ok) {
        setError(result.error ?? 'Save failed.');
        setCommitting(null);
        return;
      }
      const weeks = (turn.draft.payload as { total_weeks?: number }).total_weeks;
      await recordGenerationObservation({
        coachId: user.id,
        teamId: team?.id ?? null,
        entity: 'curriculum',
        summary: `Saved a ${weeks ?? 'multi'}-week season plan`,
        refId: result.id,
      });
      setCommitting(null);
      await loadActivePlan();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setCommitting(null);
    }
  }, [brain.turns, user, team?.id, loadActivePlan]);

  const startEdit = useCallback(() => {
    if (!plan) return;
    setDraftPlan(JSON.parse(JSON.stringify(plan)) as CurriculumPlan);
    setEditing(true);
  }, [plan]);

  const saveEdit = useCallback(async () => {
    if (!draftPlan || !planId) { setEditing(false); return; }
    setSavingEdit(true);
    setError(null);
    try {
      const { error: upErr } = await supabase
        .from('curriculum_plans')
        .update({ plan_data: draftPlan })
        .eq('id', planId);
      if (upErr) { setError(upErr.message); setSavingEdit(false); return; }
      setPlan(draftPlan);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSavingEdit(false);
    }
  }, [draftPlan, planId]);

  const turns = brain.turns.length > 0 ? brain.turns : [intro];
  const totalWeeks = useMemo(
    () => plan?.phase_plans.reduce((sum, p) => sum + (p.weekly_topics?.length ?? 0), 0) ?? 0,
    [plan],
  );

  const view = editing ? draftPlan : plan;

  return (
    <main className="main page season-page" aria-label="Plan the season">
      <header className="page-head">
        <BrainOrb size="lg" state={voice.listening ? 'listening' : brain.loading ? 'thinking' : 'idle'} ariaLabel="Brain" />
        <div>
          <span className="page-eyebrow">Layer 2 · planning</span>
          <h1 className="page-title">Season plan</h1>
          <p className="page-sub">
            {plan ? `${totalWeeks} weeks across ${plan.phase_plans.length} phases` : 'Map a multi-week theme plan, then build the weeks.'}
          </p>
        </div>
        <div className="season-head__actions">
          <Button variant="glass" size="touchline" onClick={() => navigate('/calendar')}>Calendar</Button>
        </div>
      </header>

      {/* Brain — generate / redraft / approve the season plan */}
      <section className="season-brain" aria-label="Plan with Brain">
        <div className="season-thread" aria-live="polite">
          {turns.map((turn) => {
            const isBrain = turn.role === 'assistant';
            return (
              <div key={turn.id} className={`season-turn season-turn--${isBrain ? 'brain' : 'coach'}`}>
                <span className="season-turn__speaker">
                  {isBrain && <BrainOrb size="xs" ariaLabel="Brain" />}
                  {isBrain ? 'Brain' : 'Coach'}
                </span>
                {turn.draft ? (
                  <DraftCard
                    type={turn.draft.type}
                    payload={turn.draft.payload}
                    status={committing === turn.id ? 'pending' : turn.status}
                    approveDisabled={turn.draft.type !== 'draft_curriculum'}
                    approveDisabledReason={
                      turn.draft.type !== 'draft_curriculum' ? 'Open the matching screen to save this.' : undefined
                    }
                    onApprove={() => void handleApproveCurriculum(turn.id)}
                    onDismiss={() => brain.dismissDraft(turn.id)}
                  />
                ) : (
                  <p>{turn.text}</p>
                )}
              </div>
            );
          })}
        </div>

        <BrainPrompt
          hint={brain.loading ? 'Brain is drafting…' : 'hold the orb or type — e.g. “plan the next 8 weeks, defending focus”'}
          placeholder="plan my season"
          listening={voice.listening}
          voiceStatus={voice.status}
          onMicStart={() => void voice.start()}
          onMicStop={voice.stop}
          onSubmit={(text) => void brain.send(text)}
        />
        {brain.status && <span className="season-status">{brain.status}</span>}
        {error && <Card variant="opaque"><p className="page-error">{error}</p></Card>}
      </section>

      {/* Committed plan */}
      {planLoading ? null : !view ? (
        <Card variant="opaque" className="season-empty">
          <p className="page-empty">No season plan yet. Ask Brain to “plan my season” above — then review, tweak, and build the weeks here.</p>
        </Card>
      ) : (
        <section className="season-plan" aria-label="Your season plan">
          <div className="season-plan__head">
            <div className="page-section-label">What to expect</div>
            <div className="season-plan__edit-actions">
              {editing ? (
                <>
                  <Button variant="glass" size="touchline" onClick={() => { setEditing(false); setDraftPlan(null); }}>Cancel</Button>
                  <Button variant="primary" size="touchline" onClick={() => void saveEdit()} disabled={savingEdit}>
                    {savingEdit ? 'Saving…' : 'Save changes'}
                  </Button>
                </>
              ) : (
                <Button variant="glass" size="touchline" onClick={startEdit}>Tweak</Button>
              )}
            </div>
          </div>

          {editing ? (
            <textarea
              className="season-edit__textarea"
              value={view.what_to_expect ?? ''}
              onChange={(e) => setDraftPlan((p) => (p ? { ...p, what_to_expect: e.target.value } : p))}
              rows={3}
              aria-label="What to expect"
            />
          ) : (
            view.what_to_expect && <p className="season-plan__expect">{view.what_to_expect}</p>
          )}

          {view.phase_plans.map((phase, pi) => (
            <PhaseCard
              key={`${phase.phase}-${pi}`}
              phase={phase}
              editing={editing}
              onChangeTheme={(theme) => setDraftPlan((p) => patchPhase(p, pi, { theme }))}
              onChangeRationale={(rationale) => setDraftPlan((p) => patchPhase(p, pi, { rationale }))}
              onChangeWeekTopic={(wi, topic) => setDraftPlan((p) => patchWeek(p, pi, wi, { topic }))}
              onPlanWeek={(week) => setWeekReview({ week, phaseLabel: phase.phase })}
              academy={level === 'academy'}
            />
          ))}
        </section>
      )}

      {weekReview && team && user && (
        <WeekReview
          team={team}
          coachId={user.id}
          week={weekReview.week}
          phaseLabel={weekReview.phaseLabel}
          onClose={() => setWeekReview(null)}
        />
      )}
    </main>
  );
}

function patchPhase(p: CurriculumPlan | null, pi: number, patch: Partial<PhasePlan>): CurriculumPlan | null {
  if (!p) return p;
  const phase_plans = p.phase_plans.map((ph, i) => (i === pi ? { ...ph, ...patch } : ph));
  return { ...p, phase_plans };
}

function patchWeek(p: CurriculumPlan | null, pi: number, wi: number, patch: Partial<WeeklyTopic>): CurriculumPlan | null {
  if (!p) return p;
  const phase_plans = p.phase_plans.map((ph, i) => {
    if (i !== pi) return ph;
    const weekly_topics = (ph.weekly_topics ?? []).map((w, j) => (j === wi ? { ...w, ...patch } : w));
    return { ...ph, weekly_topics };
  });
  return { ...p, phase_plans };
}

interface PhaseCardProps {
  phase: PhasePlan;
  editing: boolean;
  onChangeTheme: (v: string) => void;
  onChangeRationale: (v: string) => void;
  onChangeWeekTopic: (wi: number, v: string) => void;
  onPlanWeek: (week: WeeklyTopic) => void;
  academy: boolean;
}

function PhaseCard({ phase, editing, onChangeTheme, onChangeRationale, onChangeWeekTopic, onPlanWeek, academy }: PhaseCardProps) {
  return (
    <Card variant="glass" className="season-phase">
      <div className="season-phase__head">
        <span className="season-phase__name">{phase.phase}</span>
        {academy && (
          <span className="season-phase__corners">
            {Object.entries(phase.corner_emphasis ?? {}).map(([k, v]) => {
              const meta = CORNER_META[k];
              if (!meta || !v) return null;
              return (
                <span key={k} className="season-phase__corner" style={{ background: meta.color }} title={`${meta.label} ${v}%`}>
                  {meta.label} {v}%
                </span>
              );
            })}
          </span>
        )}
      </div>
      {editing ? (
        <>
          <input
            className="season-edit__input"
            value={phase.theme ?? ''}
            onChange={(e) => onChangeTheme(e.target.value)}
            aria-label={`${phase.phase} theme`}
            placeholder="Theme"
          />
          <textarea
            className="season-edit__textarea"
            value={phase.rationale ?? ''}
            onChange={(e) => onChangeRationale(e.target.value)}
            rows={2}
            aria-label={`${phase.phase} rationale`}
            placeholder="Why this theme now"
          />
        </>
      ) : (
        <>
          {phase.theme && <p className="season-phase__theme">{phase.theme}</p>}
          {phase.rationale && <p className="season-phase__rationale">{phase.rationale}</p>}
        </>
      )}

      <ul className="season-weeks">
        {(phase.weekly_topics ?? []).map((w, wi) => (
          <li key={`${w.week_number}-${wi}`} className="season-week">
            <span className="season-week__num">W{w.week_number}</span>
            <span className="season-week__date">{w.date_start}</span>
            {editing ? (
              <input
                className="season-edit__input season-week__topic-input"
                value={w.topic ?? ''}
                onChange={(e) => onChangeWeekTopic(wi, e.target.value)}
                aria-label={`Week ${w.week_number} topic`}
              />
            ) : (
              <span className="season-week__topic">{w.topic}</span>
            )}
            <GlassPill mono>{w.session_count}×</GlassPill>
            {!editing && (
              <Button variant="glass" size="touchline" onClick={() => onPlanWeek(w)} data-testid="plan-week">
                Plan sessions →
              </Button>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

interface WeekReviewProps {
  team: TeamFull;
  coachId: string;
  week: WeeklyTopic;
  phaseLabel: string;
  onClose: () => void;
}

/**
 * Generate + review the sessions for one curriculum week. Mirrors the
 * SessionNew approve loop per draft (decision validator → commitSessionPlan
 * → observation). Sessions stay in component state until the coach Saves
 * each — nothing half-saved hits the DB.
 */
function WeekReview({ team, coachId, week, phaseLabel, onClose }: WeekReviewProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<WeekPlanResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [existing, setExisting] = useState<number | null>(null);
  // Per-draft index → save state.
  const [committing, setCommitting] = useState<number | null>(null);
  const [savedIds, setSavedIds] = useState<Record<number, string>>({});
  const [decisionWarn, setDecisionWarn] = useState<number | null>(null);

  // Idempotency: how many sessions already exist for this curriculum week.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { count } = await supabase
          .from('sessions')
          .select('id', { count: 'exact', head: true })
          .eq('team_id', team.id)
          .eq('curriculum_week', week.week_number)
          .is('deleted_at', null);
        if (!cancelled) setExisting(count ?? 0);
      } catch {
        if (!cancelled) setExisting(0);
      }
    })();
    return () => { cancelled = true; };
  }, [team.id, week.week_number]);

  const generate = useCallback(async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    setSavedIds({});
    try {
      const res = await generateWeekSessions({ team, coachId, week, phaseLabel });
      setResult(res);
      if (res.drafts.length === 0) setError('Brain couldn’t draft this week’s sessions. Check your connection and try again.');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [team, coachId, week, phaseLabel]);

  const save = useCallback(async (index: number, opts?: { skipDecisionCheck?: boolean }) => {
    const draft = result?.drafts[index];
    if (!draft) return;
    if (!opts?.skipDecisionCheck) {
      const failures = findTechnicalDrillsMissingDecision(
        draft.drills.map((d) => ({
          type: (d.drill_type ?? 'UNKNOWN') as 'WARM_UP' | 'TECHNICAL' | 'TACTICAL' | 'SMALL_SIDED_GAME' | 'COOL_DOWN',
          decision_under_execution: d.decision_under_execution,
        })),
      );
      if (failures.length > 0) { setDecisionWarn(index); return; }
    }
    setCommitting(index);
    setError(null);
    try {
      const res = await commitSessionPlan(draft as SessionPlanDraft, { teamId: team.id, coachId });
      if (!res.ok || !res.id) { setError(res.error ?? 'Save failed.'); setCommitting(null); return; }
      setSavedIds((prev) => ({ ...prev, [index]: res.id as string }));
      await recordGenerationObservation({
        coachId,
        teamId: team.id,
        entity: 'session',
        summary: `Saved "${draft.title}" (week ${week.week_number})`,
        refId: res.id,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setCommitting(null);
    }
  }, [result, team.id, coachId, week.week_number]);

  return (
    <div className="season-review__backdrop" role="dialog" aria-modal="true" aria-label={`Plan week ${week.week_number}`}>
      <Card variant="opaque" className="season-review">
        <div className="season-review__head">
          <div>
            <span className="page-eyebrow">{phaseLabel}</span>
            <h2 className="season-review__title">Week {week.week_number} · {week.topic}</h2>
          </div>
          <Button variant="glass" size="touchline" onClick={onClose}>Close</Button>
        </div>

        {existing != null && existing > 0 && (
          <p className="season-review__warn">
            This week already has {existing} session{existing === 1 ? '' : 's'}. Generating adds more — delete the old ones on the calendar if you’re replacing them.
          </p>
        )}

        {!result && (
          <Button variant="primary" size="touchline" onClick={() => void generate()} disabled={loading} data-testid="generate-week">
            {loading ? 'Drafting sessions…' : `Generate ${week.session_count} session${week.session_count === 1 ? '' : 's'}`}
          </Button>
        )}

        {error && <p className="page-error">{error}</p>}

        {result && (
          <>
            <p className="season-review__summary">{result.weekSummary}</p>
            <div className="season-review__list">
              {result.drafts.map((draft, i) => {
                const saved = savedIds[i];
                return (
                  <div key={i} className="season-review__item">
                    <DraftCard
                      type="draft_session_plan"
                      payload={draft}
                      status={saved ? 'approved' : committing === i ? 'pending' : 'pending'}
                      approving={committing === i}
                      onApprove={() => void save(i)}
                      onDismiss={() => setResult((r) => (r ? { ...r, drafts: r.drafts.filter((_, j) => j !== i) } : r))}
                    />
                    {saved && (
                      <button type="button" className="season-review__open" onClick={() => navigate(`/sessions/${saved}`)}>
                        Open session →
                      </button>
                    )}
                    {decisionWarn === i && (
                      <Card variant="opaque" className="season-review__decision" data-verdict="amber">
                        <p>A technical drill hasn’t named the in-rep decision (Ben’s rule). Ask Brain to fix it, or save anyway.</p>
                        <div className="season-review__decision-actions">
                          <Button variant="glass" size="touchline" onClick={() => setDecisionWarn(null)}>Cancel</Button>
                          <Button variant="primary" size="touchline" onClick={() => { setDecisionWarn(null); void save(i, { skipDecisionCheck: true }); }}>
                            Save anyway
                          </Button>
                        </div>
                      </Card>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="season-review__foot">
              <Button variant="glass" size="touchline" onClick={() => void generate()} disabled={loading}>
                Regenerate week
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
